import { supabase, isSupabaseConfigured } from './supabase'
import { smartleadAPI } from './smartlead-api'

export interface DashboardMetrics {
  totalClients: number
  activeCampaigns: number
  activeTriggers: number
  activeVariants: number
  totalMailboxes: number
  healthyMailboxes: number
  activeMailboxes: number
  activeDomains: number
  todaysEmails: number
  overallReplyRate: number
  qualifiedLeads: number
  activeAlerts: number
  lastUpdated?: Date | null
  fromCache?: boolean
}

export interface SystemHealthData {
  smartleadStatus: 'healthy' | 'degraded' | 'down' | 'unknown'
  supabaseStatus: 'healthy' | 'degraded' | 'down' | 'unknown'
  responseTime: number | null
  lastSync: Date | null
  rateLimitUsage: number
  errorsLastHour: number
  lastCheck: Date
}

export interface ActivityRecord {
  id: string
  type: string
  title: string
  description?: string
  icon?: string
  timestamp: Date
}

export interface CompanyViolation {
  id: string
  companyName: string
  violationType: 'simultaneous_contact' | 'replied_overlap' | 'multiple_campaigns'
  severity: 'warning' | 'critical'
  affectedContacts: string[]
  campaigns: string[]
  recommendation: string
  status: 'active' | 'resolved' | 'ignored'
  detectedAt: Date
  resolvedAt?: Date
}

export class DashboardCacheService {
  async cacheDashboardMetrics(metrics: DashboardMetrics): Promise<void> {
    if (!isSupabaseConfigured() || !supabase) {
      console.log('Supabase not configured, skipping dashboard cache')
      return
    }

    try {
      const expiresAt = new Date(Date.now() + 15 * 60 * 1000) // 15 minutes
      
      await supabase
        .from('dashboard_metrics')
        .upsert({
          id: 'main_dashboard',
          total_clients: metrics.totalClients,
          active_campaigns: metrics.activeCampaigns,
          active_variants: metrics.activeVariants,
          total_mailboxes: metrics.totalMailboxes,
          healthy_mailboxes: metrics.healthyMailboxes,
          todays_emails: metrics.todaysEmails,
          overall_reply_rate: metrics.overallReplyRate,
          qualified_leads: metrics.qualifiedLeads,
          active_alerts: metrics.activeAlerts,
          cached_at: new Date().toISOString(),
          expires_at: expiresAt.toISOString()
        })

      console.log('💾 Dashboard metrics cached in Supabase')
    } catch (error) {
      console.error('Failed to cache dashboard metrics:', error)
    }
  }

  async getCachedDashboardMetrics(): Promise<DashboardMetrics | null> {
    if (!isSupabaseConfigured() || !supabase) {
      return null
    }

    try {
      const { data, error } = await supabase
        .from('dashboard_metrics')
        .select('*')
        .eq('id', 'main_dashboard')
        .gt('expires_at', new Date().toISOString())
        .single()

      // Suppress 406 errors - they're likely RLS policy issues and not critical
      if (error) {
        if (error.code === 'PGRST116' || error.code === '406') {
          console.debug('Dashboard metrics cache not available (RLS/policy issue)')
          return null
        }
        // Only log non-406 errors
        console.error('Failed to get cached dashboard metrics:', error)
        return null
      }
      
      if (!data) return null

      return {
        totalClients: data.total_clients,
        activeCampaigns: data.active_campaigns,
        activeVariants: data.active_variants,
        totalMailboxes: data.total_mailboxes,
        healthyMailboxes: data.healthy_mailboxes,
        todaysEmails: data.todays_emails,
        overallReplyRate: data.overall_reply_rate,
        qualifiedLeads: data.qualified_leads,
        activeAlerts: data.active_alerts,
        lastUpdated: new Date(data.cached_at),
        fromCache: true
      }
    } catch (error) {
      console.error('Failed to get cached dashboard metrics:', error)
      return null
    }
  }
}

export class SystemHealthService {
  async recordHealthCheck(healthData: Partial<SystemHealthData>): Promise<void> {
    if (!isSupabaseConfigured() || !supabase) {
      return
    }

    try {
      await supabase
        .from('system_health')
        .insert({
          smartlead_api_status: healthData.smartleadStatus || 'unknown',
          supabase_status: healthData.supabaseStatus || 'healthy',
          api_response_time: healthData.responseTime,
          last_sync_at: healthData.lastSync?.toISOString(),
          rate_limit_usage: healthData.rateLimitUsage || 0,
          errors_last_hour: healthData.errorsLastHour || 0
        })

      console.log('📊 System health recorded')
    } catch (error) {
      console.error('Failed to record system health:', error)
    }
  }

  async getSystemHealth(): Promise<SystemHealthData> {
    if (!isSupabaseConfigured() || !supabase) {
      return {
        smartleadStatus: 'unknown',
        supabaseStatus: 'unknown',
        responseTime: null,
        lastSync: null,
        rateLimitUsage: 0,
        errorsLastHour: 0,
        lastCheck: new Date()
      }
    }

    try {
      const { data, error } = await supabase
        .from('system_health')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1)
        .single()

      if (error || !data) {
        return {
          smartleadStatus: 'unknown',
          supabaseStatus: 'unknown',
          responseTime: null,
          lastSync: null,
          rateLimitUsage: 0,
          errorsLastHour: 0,
          lastCheck: new Date()
        }
      }

      return {
        smartleadStatus: data.smartlead_api_status as SystemHealthData['smartleadStatus'],
        supabaseStatus: data.supabase_status as SystemHealthData['supabaseStatus'],
        responseTime: data.api_response_time,
        lastSync: data.last_sync_at ? new Date(data.last_sync_at) : null,
        rateLimitUsage: data.rate_limit_usage,
        errorsLastHour: data.errors_last_hour,
        lastCheck: new Date(data.created_at)
      }
    } catch (error) {
      console.error('Failed to get system health:', error)
      return {
        smartleadStatus: 'unknown',
        supabaseStatus: 'unknown',
        responseTime: null,
        lastSync: null,
        rateLimitUsage: 0,
        errorsLastHour: 0,
        lastCheck: new Date()
      }
    }
  }
}

export class ActivityService {
  async addActivity(activity: { type: string; title: string; description?: string; icon?: string }): Promise<void> {
    if (!isSupabaseConfigured() || !supabase) {
      return
    }

    try {
      await supabase
        .from('recent_activity')
        .insert({
          activity_type: activity.type,
          title: activity.title,
          description: activity.description,
          icon: activity.icon
        })

      console.log('📝 Activity recorded:', activity.title)
    } catch (error) {
      console.error('Failed to record activity:', error)
    }
  }

  async getRecentActivities(limit: number = 10): Promise<ActivityRecord[]> {
    if (!isSupabaseConfigured() || !supabase) {
      return []
    }

    try {
      const { data, error } = await supabase
        .from('recent_activity')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit)

      if (error) throw error

      return (data || []).map(item => ({
        id: item.id,
        type: item.activity_type,
        title: item.title,
        description: item.description,
        icon: item.icon,
        timestamp: new Date(item.created_at)
      }))
    } catch (error) {
      console.error('Failed to get recent activities:', error)
      return []
    }
  }

  subscribeToActivities(callback: (activity: ActivityRecord) => void) {
    if (!isSupabaseConfigured() || !supabase) {
      return { unsubscribe: () => {} }
    }

    const subscription = supabase
      .channel('recent-activities')
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'recent_activity' },
        (payload) => {
          const newActivity: ActivityRecord = {
            id: payload.new.id,
            type: payload.new.activity_type,
            title: payload.new.title,
            description: payload.new.description,
            icon: payload.new.icon,
            timestamp: new Date(payload.new.created_at)
          }
          callback(newActivity)
        }
      )
      .subscribe()

    return subscription
  }
}

export class CompanyViolationService {
  async detectAndStoreViolations(leads: any[]): Promise<CompanyViolation[]> {
    const violations = this.analyzeCompanyContactViolations(leads)
    
    if (violations.length > 0) {
      await this.storeViolations(violations)
    }

    return this.formatViolations(violations)
  }

  private analyzeCompanyContactViolations(leads: any[]): any[] {
    const violations: any[] = []
    const companyMap = new Map<string, any[]>()
    
    // Group leads by company
    leads.forEach(lead => {
      if (lead.company_name) {
        if (!companyMap.has(lead.company_name)) {
          companyMap.set(lead.company_name, [])
        }
        companyMap.get(lead.company_name)!.push(lead)
      }
    })
    
    // Check for violations
    companyMap.forEach((companyLeads, companyName) => {
      if (companyLeads.length > 1) {
        const activeContacts = companyLeads.filter(lead => 
          lead.status === 'INPROGRESS' || lead.status === 'STARTED'
        )
        
        // Simultaneous contact violation
        if (activeContacts.length > 1) {
          violations.push({
            company_name: companyName,
            violation_type: 'simultaneous_contact',
            severity: 'warning',
            affected_contacts: activeContacts.map(c => c.email),
            campaigns: [...new Set(activeContacts.map(c => c.campaignId))],
            recommendation: 'Pause newer contacts until first completes sequence',
            status: 'active'
          })
        }
        
        // Replied contact overlap
        const repliedContacts = companyLeads.filter(lead => 
          lead.has_replied && lead.lead_category && this.isPositiveResponse(lead.lead_category)
        )
        
        if (repliedContacts.length > 0 && activeContacts.length > repliedContacts.length) {
          violations.push({
            company_name: companyName,
            violation_type: 'replied_overlap',
            severity: 'critical',
            affected_contacts: [
              ...repliedContacts.map(c => c.email),
              ...activeContacts.filter(c => !repliedContacts.includes(c)).map(c => c.email)
            ],
            campaigns: [...new Set(companyLeads.map(c => c.campaignId))],
            recommendation: 'Stop new outreach immediately - company already engaged',
            status: 'active'
          })
        }
      }
    })
    
    return violations
  }

  private isPositiveResponse(category: string): boolean {
    const positiveCategories = ['interested', 'positive', 'meeting', 'demo', 'qualified']
    return positiveCategories.some(pos => category.toLowerCase().includes(pos))
  }

  private async storeViolations(violations: any[]): Promise<void> {
    if (!isSupabaseConfigured() || !supabase) {
      return
    }

    try {
      await supabase
        .from('company_violations')
        .upsert(violations.map(v => ({
          company_name: v.company_name,
          violation_type: v.violation_type,
          severity: v.severity,
          affected_contacts: v.affected_contacts,
          campaigns: v.campaigns,
          recommendation: v.recommendation,
          status: v.status
        })))

      console.log(`⚠️ Stored ${violations.length} company violations`)
    } catch (error) {
      console.error('Failed to store violations:', error)
    }
  }

  private formatViolations(violations: any[]): CompanyViolation[] {
    return violations.map(v => ({
      id: v.id || crypto.randomUUID(),
      companyName: v.company_name,
      violationType: v.violation_type,
      severity: v.severity,
      affectedContacts: v.affected_contacts,
      campaigns: v.campaigns,
      recommendation: v.recommendation,
      status: v.status,
      detectedAt: new Date(),
      resolvedAt: v.resolved_at ? new Date(v.resolved_at) : undefined
    }))
  }

  async getActiveViolations(): Promise<CompanyViolation[]> {
    if (!isSupabaseConfigured() || !supabase) {
      return []
    }

    try {
      const { data, error } = await supabase
        .from('company_violations')
        .select('*')
        .eq('status', 'active')
        .order('detected_at', { ascending: false })

      if (error) throw error

      return this.formatViolations(data || [])
    } catch (error) {
      console.error('Failed to get violations:', error)
      return []
    }
  }

  subscribeToViolations(callback: (violation: CompanyViolation) => void) {
    if (!isSupabaseConfigured() || !supabase) {
      return { unsubscribe: () => {} }
    }

    const subscription = supabase
      .channel('company-violations')
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'company_violations' },
        (payload) => {
          callback(this.formatViolations([payload.new])[0])
        }
      )
      .subscribe()

    return subscription
  }
}

export class CampaignPerformanceService {
  async getCachedCampaignPerformance(campaignIds: string[]): Promise<any[]> {
    if (!isSupabaseConfigured() || !supabase) {
      return []
    }

    try {
      const { data: performanceData, error } = await supabase
        .from('campaign_performance')
        .select('*')
        .in('campaign_id', campaignIds)
        .gt('expires_at', new Date().toISOString())
        .order('cached_at', { ascending: false })

      if (error) {
        console.error('❌ Error loading performance data:', error)
        return []
      }

      return performanceData || []
    } catch (error) {
      console.error('❌ Failed to load cached performance data:', error)
      return []
    }
  }

  async cacheCampaignPerformance(campaigns: any[], analyticsMap: Map<string, any>): Promise<void> {
    if (!isSupabaseConfigured() || !supabase) {
      console.log('⚠️ Supabase not configured, skipping cache')
      return
    }

    try {
      console.log(`💾 Starting to cache ${campaigns.length} campaigns...`)
      
      const performanceData = campaigns
        .map(campaign => {
          const analytics = analyticsMap.get(campaign.id)
          const extractedData = this.extractAnalyticsData(analytics)
          
          console.log(`📊 Campaign ${campaign.id} (${campaign.name}) data:`, {
            hasAnalytics: !!analytics,
            extractedData,
            rawAnalytics: analytics
          })
          
          // Store ALL campaign data without artificial categorization
          const cacheData = {
            campaign_id: campaign.id,
            client_id: campaign.client_id?.toString(),
            group_name: campaign.name, // Use the actual campaign name
            category: campaign.category || null, // Use whatever category comes from Smartlead
            segment: null, // Let Smartlead provide this if available
            offer: null, // Let Smartlead provide this if available
            version: null, // Let Smartlead provide this if available
            country: null, // Let Smartlead provide this if available
            total_leads: extractedData.totalLeads,
            sent_count: extractedData.sentCount,
            reply_count: extractedData.replyCount,
            positive_replies: extractedData.positiveReplies,
            meetings_booked: extractedData.meetingsBooked || 0,
            open_count: extractedData.openCount || 0,
            click_count: extractedData.clickCount || 0,
            bounce_count: extractedData.bounceCount || 0,
            leads_not_started: extractedData.leadsNotStarted || 0,
            leads_in_progress: extractedData.leadsInProgress || 0,
            leads_completed: extractedData.leadsCompleted || 0,
            leads_blocked: extractedData.leadsBlocked || 0,
            leads_paused: extractedData.leadsPaused || 0,
            leads_stopped: extractedData.leadsStopped || 0,
            reply_rate: extractedData.replyRate,
            performance_rating: null, // Let the system calculate this naturally
            cached_at: new Date().toISOString(),
            expires_at: new Date(Date.now() + 4 * 60 * 60 * 1000).toISOString() // 4 hours
          }
          
          console.log(`💾 Preparing to cache campaign ${campaign.id}:`, cacheData)
          return cacheData
        })

      if (performanceData.length === 0) {
        console.log('⚠️ No campaign data to cache')
        return
      }

      console.log(`💾 Caching ${performanceData.length} campaigns to Supabase...`)
      
      const { error } = await supabase
        .from('campaign_performance')
        .upsert(performanceData)

      if (error) {
        console.error('❌ Failed to upsert campaign performance data:', error)
        throw error
      }

      console.log(`✅ Successfully cached ${performanceData.length} campaigns to Supabase`)
    } catch (error) {
      console.error('❌ Failed to cache campaign performance:', error)
      throw error
    }
  }

  async triggerManualSync(): Promise<{ success: boolean; message: string }> {
    console.log('🔄 Triggering manual background sync...')
    
    // 🔒 Security: Read from environment variables
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
    const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY
    
    if (!supabaseUrl || !supabaseKey) {
      return {
        success: false,
        message: 'Supabase configuration missing'
      }
    }
    
    try {
      const response = await fetch(`${supabaseUrl}/functions/v1/background-sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`
        },
        body: JSON.stringify({ trigger: 'manual' })
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const result = await response.json()
      console.log('✅ Manual sync triggered successfully:', result)
      
      return { 
        success: true, 
        message: result.message || 'Background sync started successfully' 
      }
    } catch (error) {
      console.error('❌ Failed to trigger manual sync:', error)
      return { 
        success: false, 
        message: error instanceof Error ? error.message : 'Failed to trigger sync' 
      }
    }
  }

  private extractAnalyticsData(analytics: any) {
    if (!analytics) {
      console.log('⚠️ No analytics data provided, using defaults')
      return {
        totalLeads: 0,
        sentCount: 0,
        replyCount: 0,
        positiveReplies: 0,
        meetingsBooked: 0,
        openCount: 0,
        clickCount: 0,
        bounceCount: 0,
        leadsNotStarted: 0,
        leadsInProgress: 0,
        leadsCompleted: 0,
        leadsBlocked: 0,
        leadsPaused: 0,
        leadsStopped: 0,
        replyRate: 0
      }
    }

    console.log('📊 Extracting analytics data from:', analytics)

    const totalLeads = parseInt(analytics.total_count) || parseInt(analytics.total_leads) || 0
    const sentCount = parseInt(analytics.sent_count) || 0
    const replyCount = parseInt(analytics.reply_count) || 0
    const positiveReplies = parseInt(analytics.positive_reply_count) || parseInt(analytics.campaign_lead_stats?.interested) || 0

    const extracted = {
      totalLeads,
      sentCount,
      replyCount,
      positiveReplies,
      meetingsBooked: parseInt(analytics.meetings_booked) || 0,
      openCount: parseInt(analytics.open_count) || 0,
      clickCount: parseInt(analytics.click_count) || 0,
      bounceCount: parseInt(analytics.bounce_count) || 0,
      leadsNotStarted: parseInt(analytics.campaign_lead_stats?.notStarted) || 0,
      leadsInProgress: parseInt(analytics.campaign_lead_stats?.inprogress) || 0,
      leadsCompleted: parseInt(analytics.campaign_lead_stats?.completed) || 0,
      leadsBlocked: parseInt(analytics.campaign_lead_stats?.blocked) || 0,
      leadsPaused: parseInt(analytics.campaign_lead_stats?.paused) || 0,
      leadsStopped: parseInt(analytics.campaign_lead_stats?.stopped) || 0,
      replyRate: sentCount > 0 ? (replyCount / sentCount) * 100 : 0
    }

    console.log('📊 Extracted analytics data:', extracted)
    return extracted
  }
}

// Export all services
export const dashboardCache = new DashboardCacheService()
export const systemHealth = new SystemHealthService()
export const activityService = new ActivityService()
export const violationService = new CompanyViolationService()
export const performanceService = new CampaignPerformanceService()
